import { View } from "@pimo/pimo-app-builder";
import { DragAndDropLayout, HeatMapChartCard } from "@pimo/pimo-components";

import { TestAppState } from "../../../app";
import { getDistributionChannelOeMatrixData } from "../get-distribution-channel-oe-matrix-data";
import { getBusinessAppCountColorScaleRanges } from "./distribution-channel-oe-matrix-color-scale";

const TITLE = "Business Apps per Distribution Channel across OEs";

// Fixed row height (channels) and a per-OE column width, so the chart keeps
// legible cells and scrolls horizontally instead of squeezing ~57 OE
// columns into the card's width (#2608).
const ROW_HEIGHT = 40;
const CHART_CHROME_HEIGHT = 130;
const COLUMN_WIDTH = 70;
const ROW_LABEL_ALLOWANCE = 160;

/**
 * Adds the "Business Apps per Distribution Channel across OEs" heat map
 * card: rows are every configured distribution channel, columns are every
 * OE, cells are the number of business apps in that channel/OE combination
 * (#2608).
 */
export function addDistributionChannelOeMatrixCard(
  view: View<TestAppState, DragAndDropLayout>
) {
  const component = view.addComponent({
    component: HeatMapChartCard,
    layoutProps: {
      componentId: "distributionChannelOeMatrixDashboardComponent",
      labels: ["heatMap", "chart", "distributionChannel"],
      description:
        "Shows the number of business apps per distribution channel across OEs.",
      title: TITLE,
    },
  });

  component.mapState(({ dashboard, settings }) => {
    const { rows, oes, columnTotals, grandTotal } =
      getDistributionChannelOeMatrixData(
        dashboard?.projects,
        settings?.configuration?.distributionChannelDefinition
      );

    const series = rows.map((row) => ({
      name: row.channelLabel,
      data: oes.map((oe) => ({
        x: oe.label,
        y: row.countsByOeId[oe.id] ?? 0,
      })),
    }));

    return {
      title: TITLE,
      series,
      colorScaleRanges: getBusinessAppCountColorScaleRanges(),
      dataLabelFormatter: (value: number) =>
        value === 0 ? "·" : String(value),
      showLegend: false,
      height: rows.length * ROW_HEIGHT + CHART_CHROME_HEIGHT,
      width: oes.length * COLUMN_WIDTH + ROW_LABEL_ALLOWANCE,
      rowTotals: rows.map((row) => ({
        name: row.channelLabel,
        total: row.total,
      })),
      columnTotals: oes.map((oe) => ({
        name: oe.label,
        total: columnTotals[oe.id] ?? 0,
      })),
      grandTotal,
    };
  });
}
